import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Loader2, LogOut, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const fmt = (n) =>
  n == null ? "-" : new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(n);

const inputCls =
  "w-full border border-gold/20 bg-white/[0.04] px-4 py-3 text-sm text-white outline-none transition-all placeholder:text-slate-500 focus:border-gold";

const numCls = "w-24 border border-gold/20 bg-white/[0.04] px-3 py-2 text-sm text-white outline-none focus:border-gold";

const HORIZONTE_LABELS = { "<1": "<1 año", "1-3": "1-3 años", "3-5": "3-5 años", ">3": ">3 años", curiosidad: "Solo curiosidad" };

const TABS = [
  ["leads", "Leads"],
  ["config", "Múltiplos y fiscal"],
  ["financiacion", "Parámetros de financiación"],
  ["adquisicion", "Parámetros de adquisición"],
  ["test", "Test de venta"],
];

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    document.title = "Panel de administración | AJG M&A Consulting";
    axios
      .get(`${API}/auth/me`)
      .then(({ data }) => setUser(data))
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }
  if (!user) return <Login onSuccess={setUser} />;
  return <Dashboard onLogout={() => setUser(false)} />;
}

function Login({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { username, password });
      onSuccess(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Error de acceso");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6" data-testid="admin-login">
      <div className="w-full max-w-sm border border-gold/20 bg-ink-3/60 p-10">
        <img src="/logo-light.png" alt="AJG M&A Consulting" className="mx-auto h-16 w-auto" />
        <h1 className="mt-8 text-center font-serif text-2xl text-white">Panel de administración</h1>
        <form onSubmit={submit} className="mt-8 space-y-5">
          <input data-testid="admin-username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Usuario" autoComplete="username" className={inputCls} />
          <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Contraseña" autoComplete="current-password" className={inputCls} />
          <button type="submit" disabled={loading} data-testid="admin-login-submit" className="flex w-full items-center justify-center gap-2 bg-gold px-6 py-3.5 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-all hover:bg-gold-light disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Entrar"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("leads");

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    onLogout();
  };

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-gold/10">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-5">
          <div className="flex flex-wrap items-center gap-4">
            <img src="/logo-light.png" alt="AJG M&A Consulting" className="h-11 w-auto" />
            <nav className="flex flex-wrap gap-2">
              {TABS.map(([k, l]) => (
                <button
                  key={k}
                  data-testid={`admin-tab-${k}`}
                  onClick={() => setTab(k)}
                  className={`px-4 py-2 text-[12px] font-semibold uppercase tracking-[0.15em] transition-colors ${
                    tab === k ? "bg-gold text-ink" : "text-slate-300 hover:text-gold"
                  }`}
                >
                  {l}
                </button>
              ))}
            </nav>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-300 transition-colors hover:text-gold">
            <LogOut className="h-4 w-4" /> Salir
          </button>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-6 py-10">
        {tab === "leads" && <LeadsTab />}
        {tab === "config" && <ConfigTab />}
        {tab === "financiacion" && <GenericSectionTab section="financiacion" title="Parámetros de financiación" testid="admin-financiacion" />}
        {tab === "adquisicion" && <GenericSectionTab section="adquisicion" title="Parámetros de adquisición" testid="admin-adquisicion" />}
        {tab === "test" && <TestTab />}
      </main>
    </div>
  );
}

function useConfig() {
  const [config, setConfig] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/config`).then(({ data }) => setConfig(data));
  }, []);
  return [config, setConfig];
}

function SaveButton({ config, label = "Guardar parámetros" }) {
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/config`, config);
      toast.success("Parámetros guardados");
    } catch {
      toast.error("No se pudieron guardar los parámetros");
    } finally {
      setSaving(false);
    }
  };
  return (
    <button onClick={save} disabled={saving} data-testid="admin-config-save" className="flex items-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-all hover:bg-gold-light disabled:opacity-60">
      <Save className="h-4 w-4" /> {saving ? "Guardando..." : label}
    </button>
  );
}

function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [config] = useConfig();
  const [fOrigen, setFOrigen] = useState("");
  const [fSector, setFSector] = useState("");
  const [fHorizonte, setFHorizonte] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (fOrigen) params.origen = fOrigen;
    if (fSector) params.sector = fSector;
    if (fHorizonte) params.horizonte = fHorizonte;
    const { data } = await axios.get(`${API}/admin/leads`, { params });
    setLeads(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [fOrigen, fSector, fHorizonte]);

  const exportCsv = async () => {
    const { data } = await axios.get(`${API}/admin/leads.csv`, {
      params: fOrigen ? { origen: fOrigen } : {},
      responseType: "blob",
    });
    const url = URL.createObjectURL(data);
    const aEl = document.createElement("a");
    aEl.href = url;
    aEl.download = "leads-ajg.csv";
    aEl.click();
    URL.revokeObjectURL(url);
  };

  const selectCls = "border border-gold/20 bg-ink-2 px-3 py-2.5 text-sm text-white outline-none focus:border-gold";

  return (
    <div data-testid="admin-leads">
      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="admin-filter-origen" value={fOrigen} onChange={(e) => setFOrigen(e.target.value)} className={selectCls}>
          <option value="">Todos los orígenes</option>
          {[["valoracion", "Valoración"], ["fiscal", "Fiscal"], ["circulante", "Circulante"], ["compra", "Compra"], ["test_venta", "Test de venta"], ["sensibilidad", "Sensibilidad"]].map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select data-testid="admin-filter-sector" value={fSector} onChange={(e) => setFSector(e.target.value)} className={selectCls}>
          <option value="">Todos los sectores</option>
          {config && Object.keys(config.multiples).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="admin-filter-horizonte" value={fHorizonte} onChange={(e) => setFHorizonte(e.target.value)} className={selectCls}>
          <option value="">Todos los horizontes</option>
          {Object.entries(HORIZONTE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <button onClick={exportCsv} data-testid="admin-export-csv" className="ml-auto flex items-center gap-2 border border-gold/60 px-4 py-2.5 text-[12px] font-semibold uppercase tracking-[0.15em] text-gold transition-all hover:bg-gold hover:text-ink">
          <Download className="h-4 w-4" /> Exportar CSV
        </button>
      </div>

      <div className="mt-6 overflow-x-auto border border-gold/15">
        <table className="w-full min-w-[1100px] text-left text-sm">
          <thead>
            <tr className="border-b border-gold/15 bg-ink-3/60 text-[11px] uppercase tracking-[0.15em] text-gold">
              {["Fecha", "Origen", "Nombre", "Empresa", "Contacto", "Sector", "EBITDA norm.", "EV ajustado", "Índice", "Horizonte", "Seguimiento"].map((h) => (
                <th key={h} className="px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="admin-leads-body">
            {loading ? (
              <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400">Cargando...</td></tr>
            ) : leads.length === 0 ? (
              <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400">Sin leads todavía.</td></tr>
            ) : (
              leads.map((l) => (
                <tr key={l.id} className="border-b border-gold/10 text-slate-300 transition-colors hover:bg-gold/[0.04]" data-testid={`admin-lead-${l.id}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{new Date(l.fecha).toLocaleDateString("es-ES")}</td>
                  <td className="px-4 py-3">
                    <span className="border border-gold/40 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-gold">{l.origen}</span>
                  </td>
                  <td className="px-4 py-3 font-medium text-white">{l.nombre}</td>
                  <td className="px-4 py-3">{l.empresa || "-"}</td>
                  <td className="px-4 py-3 text-xs">{l.email}<br />{l.telefono}</td>
                  <td className="px-4 py-3">{l.sector}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{l.ebitda_norm != null ? `${fmt(l.ebitda_norm)} €` : "-"}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{l.ev_min != null ? `${fmt(l.ev_min)} – ${fmt(l.ev_max)} €` : "-"}</td>
                  <td className="px-4 py-3">{l.indice_calidad != null ? l.indice_calidad : "-"}</td>
                  <td className="px-4 py-3">{HORIZONTE_LABELS[l.horizonte_venta] || "-"}</td>
                  <td className="px-4 py-3">
                    {l.lead_medio_plazo && (
                      <span className="border border-emerald-400/50 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-emerald-300" data-testid={`admin-medio-${l.id}`}>
                        Medio plazo
                      </span>
                    )}
                    {l.consent_comercial && <span className="ml-1 text-[10px] uppercase text-slate-500">Comercial</span>}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-xs text-slate-500">{leads.length} lead{leads.length === 1 ? "" : "s"}</p>
    </div>
  );
}

function ConfigTab() {
  const [config, setConfig] = useConfig();
  if (!config) return <Loader2 className="h-8 w-8 animate-spin text-gold" />;

  const setMultiple = (sector, idx, value) => {
    const m = { ...config.multiples };
    m[sector] = [...m[sector]];
    m[sector][idx] = Number(value);
    setConfig({ ...config, multiples: m });
  };

  return (
    <div className="space-y-12" data-testid="admin-config">
      <section>
        <h2 className="font-serif text-2xl text-white">Múltiplos EV/EBITDA por sector</h2>
        <div className="mt-6 grid gap-3 md:grid-cols-2">
          {Object.entries(config.multiples).map(([sector, pair]) => (
            <div key={sector} className="flex items-center justify-between gap-3 border border-gold/10 bg-ink-3/40 px-4 py-3">
              <span className="text-sm text-slate-300">{sector}</span>
              <div className="flex items-center gap-2">
                <input type="number" step="0.1" value={pair[0]} onChange={(e) => setMultiple(sector, 0, e.target.value)} className={numCls} data-testid={`admin-mult-min-${sector}`} />
                <span className="text-gold">–</span>
                <input type="number" step="0.1" value={pair[1]} onChange={(e) => setMultiple(sector, 1, e.target.value)} className={numCls} data-testid={`admin-mult-max-${sector}`} />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-serif text-2xl text-white">Ajuste por tamaño (según EBITDA normalizado)</h2>
        <div className="mt-6 grid gap-3 md:grid-cols-2">
          {config.size_adjustments.map((tier, i) => (
            <div key={i} className="flex items-center justify-between gap-3 border border-gold/10 bg-ink-3/40 px-4 py-3">
              <span className="text-sm text-slate-300">
                {tier.max_ebitda == null ? "> 3.000.000 €" : `Hasta ${fmt(tier.max_ebitda)} €`}
              </span>
              <input type="number" step="0.1" value={tier.adj} onChange={(e) => {
                const s = [...config.size_adjustments];
                s[i] = { ...s[i], adj: Number(e.target.value) };
                setConfig({ ...config, size_adjustments: s });
              }} className={numCls} data-testid={`admin-size-${i}`} />
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-serif text-2xl text-white">Parámetros fiscales (calculadora fiscal)</h2>
        <div className="mt-6 grid gap-3 md:grid-cols-2">
          <div className="flex items-center justify-between gap-3 border border-gold/10 bg-ink-3/40 px-4 py-3">
            <span className="text-sm text-slate-300">Tipo Impuesto sobre Sociedades (%)</span>
            <input type="number" step="1" value={config.fiscal.tipo_is * 100} onChange={(e) => setConfig({ ...config, fiscal: { ...config.fiscal, tipo_is: Number(e.target.value) / 100 } })} className={numCls} data-testid="admin-tipo-is" />
          </div>
          <div className="flex items-center justify-between gap-3 border border-gold/10 bg-ink-3/40 px-4 py-3">
            <span className="text-sm text-slate-300">Exención art. 21 LIS (%)</span>
            <input type="number" step="1" value={config.fiscal.exencion_pct * 100} onChange={(e) => setConfig({ ...config, fiscal: { ...config.fiscal, exencion_pct: Number(e.target.value) / 100 } })} className={numCls} data-testid="admin-exencion" />
          </div>
        </div>
      </section>

      <SaveButton config={config} />
    </div>
  );
}

function GenericSectionTab({ section, title, testid }) {
  const [config, setConfig] = useConfig();
  if (!config) return <Loader2 className="h-8 w-8 animate-spin text-gold" />;
  const data = config[section];

  const update = (path, value) => {
    const next = JSON.parse(JSON.stringify(config));
    let ref = next[section];
    for (let i = 0; i < path.length - 1; i++) ref = ref[path[i]];
    ref[path[path.length - 1]] = value;
    setConfig(next);
  };

  const renderValue = (value, path, label) => {
    const key = path.join("-");
    if (typeof value === "number") {
      return (
        <div key={key} className="flex items-center justify-between gap-3 border border-gold/10 bg-ink-3/40 px-4 py-3">
          <span className="text-sm capitalize text-slate-300">{label.replace(/_/g, " ")}</span>
          <input type="number" step="0.01" value={value} onChange={(e) => update(path, Number(e.target.value))} className={numCls} data-testid={`${testid}-${key}`} />
        </div>
      );
    }
    if (typeof value === "string") {
      return (
        <div key={key} className="flex flex-col gap-2 border border-gold/10 bg-ink-3/40 px-4 py-3">
          <span className="text-sm capitalize text-slate-300">{label.replace(/_/g, " ")}</span>
          <input type="text" value={value} onChange={(e) => update(path, e.target.value)} className={inputCls} data-testid={`${testid}-${key}`} />
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-10" data-testid={testid}>
      <h2 className="font-serif text-2xl text-white">{title}</h2>
      {section === "financiacion" && (
        <section>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">Índices de referencia</h3>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {renderValue(data.indices.euribor_12m, ["indices", "euribor_12m"], "Euríbor 12M (%)")}
            {renderValue(data.indices.euribor_3m, ["indices", "euribor_3m"], "Euríbor 3M (%)")}
            {renderValue(data.indices.actualizado, ["indices", "actualizado"], "Fecha de actualización")}
          </div>
        </section>
      )}
      {Object.entries(data).filter(([k]) => k !== "indices" && k !== "sector_refs").map(([key, obj]) => (
        typeof obj === "object" && obj !== null ? (
          <section key={key}>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gold">{obj.nombre || key.replace(/_/g, " ")}</h3>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {Object.entries(obj).filter(([k]) => k !== "nombre").map(([k, v]) => renderValue(v, [key, k], k))}
            </div>
          </section>
        ) : renderValue(obj, [key], key)
      ))}
      <SaveButton config={config} />
    </div>
  );
}

function TestTab() {
  const [config] = useConfig();
  const [json, setJson] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) setJson(JSON.stringify(config.test_venta, null, 2));
  }, [config]);

  if (!config) return <Loader2 className="h-8 w-8 animate-spin text-gold" />;

  const save = async () => {
    let parsed;
    try {
      parsed = JSON.parse(json);
      if (!Array.isArray(parsed.questions) || parsed.questions.length !== 15) {
        toast.error("El test debe tener exactamente 15 preguntas.");
        return;
      }
    } catch {
      toast.error("JSON no válido. Revisa la sintaxis.");
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API}/admin/config`, { ...config, test_venta: parsed });
      toast.success("Test de venta guardado");
    } catch {
      toast.error("No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-test">
      <h2 className="font-serif text-2xl text-white">Test de venta · preguntas, puntuaciones y textos</h2>
      <p className="text-sm leading-relaxed text-slate-400">
        Edita el JSON: cada pregunta tiene <code className="text-gold">texto</code>, <code className="text-gold">opciones</code> (con puntos),
        el texto <code className="text-gold">porque</code> (por qué lo penaliza un comprador) y <code className="text-gold">accion</code> (recomendación del informe).
        Los niveles del índice global están en <code className="text-gold">niveles</code>.
      </p>
      <textarea
        value={json}
        onChange={(e) => setJson(e.target.value)}
        rows={28}
        data-testid="admin-test-json"
        className="w-full border border-gold/20 bg-ink-2 p-5 font-mono text-xs leading-relaxed text-slate-200 outline-none focus:border-gold"
      />
      <button onClick={save} disabled={saving} data-testid="admin-test-save" className="flex items-center gap-3 bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-ink transition-all hover:bg-gold-light disabled:opacity-60">
        <Save className="h-4 w-4" /> {saving ? "Guardando..." : "Guardar test"}
      </button>
    </div>
  );
}
